import { canTransition } from './transition.js';
/**
 * Order lifecycle. Appendix C.1, Appendix W.2 and §17 `OPS-105`.
 *
 * > `draft → quoted → confirmed → partially_paid → paid → fulfilled → closed`
 * >
 * > Also `draft|quoted → expired` · any pre-fulfilled → `cancelled` · `paid|fulfilled → refunded` ·
 * > `confirmed → on_hold → confirmed`. Cancellation after `confirmed` requires a reason and a
 * > refund decision (`OPS-105`).
 *
 * ---
 *
 * **Written late, and that is the finding.** Appendix C.2's job machine, C.3's remake, C.4's
 * prescription, C.7's cash session and C.12's tenant all live here beside their transition tables.
 * C.1 did not: its guards grew inside `payments.service.ts` and `orders.service.ts` as inline
 * status comparisons, so the set of legal order states existed only as whatever strings those
 * comparisons happened to mention.
 *
 * The visible cost was that `sales_order.status` had no registry to point a check constraint at,
 * which is Appendix H's requirement for every enum and the same gap that let `sale_type` accept a
 * transposed value for three milestones. The invisible cost is that two services each decided
 * separately what a payable order is.
 *
 * This is the list and the table. The services' guards are not rewritten to use it here, because
 * they answer a narrower question than "is this edge legal" (which refusal message a counter
 * should see, and `POS-002`'s deposit ordering), and moving them is its own change.
 */
export const ORDER_STATUSES = [
    'draft',
    'quoted',
    'confirmed',
    'partially_paid',
    'paid',
    'fulfilled',
    'closed',
    'expired',
    'cancelled',
    'refunded',
    'on_hold',
];
/**
 * Appendix C.1's edges.
 *
 * `cancelled` is reachable from "any pre-fulfilled", which is every state before the goods are
 * handed over, and deliberately not from `fulfilled`: once a customer has the glasses the operation
 * is a refund, and `POS-005` keeps those distinct with distinct permissions. `refunded` is reachable
 * only from `paid` and `fulfilled`, which is the same rule read from the other end.
 */
export const ORDER_TRANSITIONS = {
    draft: ['quoted', 'confirmed', 'expired', 'cancelled'],
    quoted: ['confirmed', 'expired', 'cancelled'],
    /**
     * **`confirmed -> fulfilled` and `partially_paid -> fulfilled` are udhaar, and C.1 does not draw
     * them.**
     *
     * The appendix writes one chain, `confirmed -> partially_paid -> paid -> fulfilled`, which reads
     * as though an order is paid before the customer takes the glasses. In this market it frequently
     * is not: `PK-023`'s udhaar ledger is "a running customer balance", and `ACC-002` recognises
     * revenue at collection while `postCollection` books the unpaid remainder to
     * `accounts_receivable`. A shop hands over the spectacles and the balance stays on the book.
     *
     * Writing the table from the arrows alone made collection on udhaar illegal, which six existing
     * tests caught immediately. The edges are real; the appendix's chain is the happy path drawn as
     * if it were the only one. Recorded as a spec problem rather than resolved by weakening the
     * guard, because the guard is what stops a cancelled order being collected back to life.
     *
     * `partially_paid` is still reachable only from `confirmed`: an order takes money after it is
     * agreed and invoiced, which is what the smoke walkthrough found being skipped.
     */
    confirmed: ['partially_paid', 'paid', 'fulfilled', 'on_hold', 'cancelled'],
    partially_paid: ['paid', 'fulfilled', 'cancelled'],
    paid: ['fulfilled', 'cancelled', 'refunded'],
    // A fulfilled order is not cancellable. The customer has the goods.
    fulfilled: ['closed', 'refunded'],
    on_hold: ['confirmed', 'cancelled'],
    refunded: ['closed'],
    closed: [],
    expired: [],
    cancelled: [],
};
export function checkOrderTransition(from, to) {
    if (!canTransition(ORDER_TRANSITIONS, from, to)) {
        return {
            allowed: false,
            code: 'ORDER_TRANSITION_INVALID',
            message: `An order cannot go from ${from} to ${to} (Appendix C.1).`,
        };
    }
    return { allowed: true };
}
//# sourceMappingURL=order.js.map